import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiExternalLink, FiCalendar, FiMessageSquare } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import FaceAvatar from '../common/FaceAvatar';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';

const EventCard = ({ title, date, location, category, type, color, image }) => {
  const typeColors = {
    'In-Person': 'text-emerald-600 bg-emerald-50/50 border-emerald-200/50',
    'Hybrid': 'text-amber-600 bg-amber-50/50 border-amber-200/50',
    'Virtual': 'text-indigo-600 bg-indigo-50/50 border-indigo-200/50'
  };

  return (
    <div className="glass-card p-4 flex items-center gap-4 mb-4 w-full min-w-[240px] max-w-[300px] rounded-2xl inner-glow group">
      <div className="w-16 h-16 rounded-xl shrink-0 overflow-hidden relative shadow-inner">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className={`absolute inset-0 bg-${color}-500/10`}></div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className={`text-[7px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border ${typeColors[type] || 'text-slate-500 bg-slate-50'}`}>
            {type}
          </span>
          <span className="text-[7px] font-bold uppercase tracking-widest text-slate-400 bg-white/40 px-2 py-0.5 rounded-full border border-white/50">
            {category}
          </span>
        </div>
        <h4 className="text-[12px] font-black text-slate-900 truncate leading-tight mb-1.5">{title}</h4>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium">
            <SafeIcon name="Calendar" className="w-3 h-3 text-indigo-500/70" /> {date}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium">
            <SafeIcon name="MapPin" className="w-3 h-3 text-indigo-500/70" /> {location}
          </div>
        </div>
      </div>
    </div>
  );
};

const VerticalCarousel = ({ reverse = false, duration = 25 }) => {
  const { t } = useLanguage();
  const events = [
    { title: t("hero.events.summit"), date: "Oct 12", location: "SF", category: "Tech", type: "In-Person", color: "indigo", image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=200" },
    { title: t("hero.events.jazz"), date: "Nov 05", location: "NO", category: "Music", type: "Hybrid", color: "purple", image: "https://images.unsplash.com/photo-1482440308425-276ad0f28b19?auto=format&fit=crop&q=80&w=200" },
    { title: t("hero.events.design"), date: "Sep 28", location: "LDN", category: "Design", type: "In-Person", color: "emerald", image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&q=80&w=200" },
    { title: t("hero.events.crypto"), date: "Dec 01", location: "DBX", category: "Finance", type: "Virtual", color: "amber", image: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?auto=format&fit=crop&q=80&w=200" },
    { title: t("hero.events.wellness"), date: "Aug 15", location: "BAL", category: "Health", type: "In-Person", color: "rose", image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=200" },
    { title: t("hero.events.saas"), date: "Jan 10", location: "BER", category: "SaaS", type: "Hybrid", color: "cyan", image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=200" },
  ];
  const displayEvents = [...events, ...events];
  return (
    <div className="h-[580px] overflow-hidden relative mask-fade-y">
      <motion.div
        initial={{ y: reverse ? "-50%" : "0%" }}
        animate={{ y: reverse ? "0%" : "-50%" }}
        transition={{ repeat: Infinity, ease: "linear", duration: duration }}
        className="flex flex-col gap-3 py-4"
      >
        {displayEvents.map((event, idx) => (
          <EventCard key={idx} {...event} />
        ))}
      </motion.div>
    </div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isTalkModalOpen, setIsTalkModalOpen] = useState(false);

  useEffect(() => {
    if (isDemoModalOpen || isTalkModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDemoModalOpen, isTalkModalOpen]);

  return (
    <section className="relative pt-[115px] pb-[72px] lg:pt-[173px] lg:pb-[115px] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-indigo-100/30 rounded-full blur-[120px] -z-10" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-violet-100/20 rounded-full blur-[120px] -z-10" />
      
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/50 backdrop-blur-md border border-white/50 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-6 shadow-sm">
              <SafeIcon name="Zap" className="w-4 h-4" /> {t('hero.badge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] mb-6 tracking-tight">
              {t('hero.title1')} <br className="hidden lg:block"/>
              <span className="text-gradient">{t('hero.title2')}</span> <br className="hidden lg:block"/>
              {t('hero.title3')}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t('hero.desc')}
            </p>
            <div className="mb-12">
              <div className="flex flex-col sm:flex-row gap-4 mb-3">
                <a 
                  href="https://app.bookmeinscotty.com/add-item"
                  onClick={() => trackEvent('cta_click', { cta: 'launch_app', section: 'hero' })}
                  className="bg-gradient-primary text-white px-8 py-4 rounded-full font-bold flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-indigo-500/40 transition-all transform hover:-translate-y-1 inner-glow animate-pulse-subtle"
                >
                  {t('hero.ctaLaunch')} <SafeIcon name="ArrowRight" className="w-5 h-5" />
                </a>
                <div className="relative w-full sm:w-auto flex">
                  <div
                    className="absolute -inset-[1.5px] z-0 pointer-events-none"
                    style={{
                      borderRadius: '9999px',
                      background: 'conic-gradient(from var(--angle-talk, 0deg), #6366f1, #8b5cf6, #ec4899, #f59e0b, #6366f1)',
                      animation: 'spin-border-talk 2.5s linear infinite',
                    }}
                  />
                  <style dangerouslySetInnerHTML={{ __html: `
                    @property --angle-talk {
                      syntax: '<angle>';
                      initial-value: 0deg;
                      inherits: false;
                    }
                    @keyframes spin-border-talk {
                      to { --angle-talk: 360deg; }
                    }
                  `}} />
                  <button
                    onClick={() => {
                      trackEvent('cta_click', { cta: 'talk_to_us', section: 'hero' });
                      setIsTalkModalOpen(true);
                    }}
                    className="relative z-10 w-full p-[1.5px] rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 active:scale-95 transition-all shadow-sm hover:shadow-indigo-500/20"
                  >
                    <div className="w-full bg-white/90 backdrop-blur-md hover:bg-white/95 text-slate-800 px-[30px] py-[14.5px] rounded-full font-bold flex items-center justify-center gap-2 transition-all">
                      <SafeIcon name="MessageSquare" className="w-5 h-5 text-indigo-600" /> {t('hero.ctaTalk')}
                    </div>
                  </button>
                </div>
                <button 
                  onClick={() => {
                    trackEvent('cta_click', { cta: 'book_a_demo', section: 'hero' });
                    setIsDemoModalOpen(true);
                  }}
                  className="bg-white/60 backdrop-blur-md text-slate-700 border border-white/60 px-8 py-4 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white/80 transition-all shadow-sm active:scale-95"
                >
                  <SafeIcon name="PlayCircle" className="w-5 h-5 text-indigo-600" /> {t('hero.ctaDemo')}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 font-medium pl-4 select-none">
                * {t('hero.ctaAnnotation')}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-200/60">
              <div>
                <div className="flex -space-x-2 mb-2">
                  <FaceAvatar index={0} className="w-10 h-10 rounded-full border-2 border-slate-50" />
                  <FaceAvatar index={1} className="w-10 h-10 rounded-full border-2 border-slate-50" />
                  <FaceAvatar index={2} className="w-10 h-10 rounded-full border-2 border-slate-50" />
                  <FaceAvatar index={3} className="w-10 h-10 rounded-full border-2 border-slate-50" />
                </div>
                <div className="text-3xl font-bold text-slate-900">100+</div>
                <div className="text-xs text-slate-500 font-bold tracking-widest uppercase">{t('hero.stats.organizers')}</div>
              </div>
              <div>
                <div className="h-8 mb-2"></div>
                <div className="text-3xl font-bold text-slate-900">0%</div>
                <div className="text-xs text-slate-500 font-bold tracking-widest uppercase">{t('hero.stats.freeFees')}</div>
              </div>
              <div>
                <div className="h-8 mb-2"></div>
                <div className="text-3xl font-bold text-slate-900">1.99%</div>
                <div className="text-xs text-slate-500 font-bold tracking-widest uppercase">{t('hero.stats.lowestPaid')}</div>
              </div>
            </div>
          </motion.div>

          {/* Fixed Width Container for 2 Columns */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative hidden lg:flex items-center justify-center w-full">
            <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 to-purple-500/10 rounded-full blur-3xl -z-10"></div>
            
            {/* Floating Stat Badge */}
            <motion.div 
              animate={{ y: [0, -12, 0] }} 
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }} 
              className="absolute -top-8 -right-8 glass-panel p-6 rounded-[24px] shadow-2xl flex items-center gap-4 z-20 inner-glow border-white/60"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100/50 backdrop-blur-sm flex items-center justify-center text-emerald-600 border border-emerald-200/50">
                <SafeIcon name="EventTicket" className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">{t('hero.stats.ticketsSales')}</div>
                <div className="text-lg font-black text-slate-900">3,901</div>
              </div>
            </motion.div>

            {/* Symmetrical glass panel container */}
            <div className="flex gap-6 p-6 glass-panel rounded-[48px] inner-glow w-full">
              <div className="flex-1">
                <VerticalCarousel reverse={false} duration={35} />
              </div>
              <div className="flex-1 mt-16">
                <VerticalCarousel reverse={true} duration={30} />
              </div>
            </div>

            {/* Floating Stat Badge */}
            <motion.div 
              animate={{ y: [0, -12, 0] }} 
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }} 
              className="absolute -bottom-8 -left-8 glass-panel p-6 rounded-[24px] shadow-2xl flex items-center gap-4 z-20 inner-glow border-white/60"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100/50 backdrop-blur-sm flex items-center justify-center text-emerald-600 border border-emerald-200/50">
                <SafeIcon name="TrendingUp" className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">{t('hero.stats.dailyRevenue')}</div>
                <div className="text-lg font-black text-slate-900">€12,490.00</div>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .mask-fade-y {
          mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
          -webkit-mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
        }
      `}} />

      {/* Book a Demo Modal */}
      <AnimatePresence>
        {isDemoModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Body */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-screen-2xl h-[92vh] flex flex-col overflow-hidden z-10"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{t('hero.ctaDemo')}</h3>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href="https://tidycal.com/team/bookmeinevents/book-a-demo" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                    title="Open in new tab"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => setIsDemoModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Webview / Iframe container */}
              <div className="flex-1 bg-slate-50 relative">
                <iframe
                  src="https://tidycal.com/team/bookmeinevents/book-a-demo"
                  title="Book a Demo"
                  className="w-full h-full border-0 absolute inset-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Talk to us Modal */}
      <AnimatePresence>
        {isTalkModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setIsTalkModalOpen(false)}
            />

            {/* Modal Body */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden z-10"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{t('hero.ctaTalk')}</h3>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href="https://survey.bookmein.events/vnU57U" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                    title="Open in new tab"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => setIsTalkModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Webview / Iframe container */}
              <div className="flex-1 bg-slate-50 relative">
                <iframe
                  src="https://survey.bookmein.events/vnU57U"
                  title="Talk to us"
                  className="w-full h-full border-0 absolute inset-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </section>
  );
};

export default Hero;